/* ORIO Cassetto digitale — shared parts (upload, documents, reminders, weight)
   - Warm light theme (no more dark glass artifacts)
   - Hero stats bar with counters
   - Critical alert banner for expired antiparassitario/vaccino
   - Next-due hero card animated
   - Documents list with per-item urgency badges + PDF export button
   - Promemoria with quick-add chips
*/
import { createPortal } from "react-dom";
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Plus, Trash2, FileText, Calendar, Bell, Check, Syringe, Bug, Stethoscope,
  BookOpen, ScanLine, Wrench, AlertTriangle, Download, ShieldCheck, Clock,
  TrendingUp, Heart, BellRing, Sparkles, X, Camera, Search, Zap, Loader2,
  Weight, Crown,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { api, formatErr } from "@/lib/api";
import { hapticTap, hapticSuccess } from "@/lib/haptics";
import { useAuth } from "@/context/AuthContext";
import Paywall from "@/components/Paywall";
import { dispatchOrio, ORIO_EVENTS } from "@/lib/events";

import { DOC_CATS, DOC_CAT_LABEL, EXPENSE_BY } from "./constants";

const REM_TYPES = [
  { id: "vaccino",          label: "Vaccino",            icon: Syringe,     color: "#34D399" },
  { id: "antiparassitario", label: "Antiparassitario",   icon: Bug,         color: "#FFB020" },
  { id: "visita",           label: "Visita veterinaria", icon: Stethoscope, color: "#FF7A1A" },
  { id: "libretto",         label: "Libretto sanitario", icon: BookOpen,    color: "#9aa0a6" },
  { id: "microchip",        label: "Microchip",          icon: ScanLine,    color: "#7B61FF" },
  { id: "tagliando",        label: "Tagliando / cura",   icon: Wrench,      color: "#0EA5E9" },
  { id: "altro",            label: "Altro",              icon: Bell,        color: "#9aa0a6" },
];
export const REM_BY = Object.fromEntries(REM_TYPES.map((t) => [t.id, t]));

const ADD_MONTHS = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return d.toISOString().slice(0, 10);
};

const QUICK_REMINDERS = [
  { type: "antiparassitario", title: "Antiparassitario mensile", inMonths: 1, icon: Bug, color: "#FFB020" },
  { type: "antiparassitario", title: "Antiparassitario trimestrale", inMonths: 3, icon: Bug, color: "#FFB020" },
  { type: "vaccino",          title: "Richiamo annuale vaccino", inMonths: 12, icon: Syringe, color: "#34D399" },
  { type: "visita",           title: "Controllo dal veterinario", inMonths: 6, icon: Stethoscope, color: "#FF7A1A" },
];

/* ───── DOCUMENTS LIST ───── */
export function DocumentsList({ docs, daysTo, onDelete }) {
  if (docs.length === 0) {
    return (
      <div className="rounded-3xl p-10 text-center bg-white border border-[#F0EBE3]" data-testid="docs-empty">
        <FileText className="h-9 w-9 mx-auto text-[#C7C0B3]" />
        <div className="mt-3 text-[#1A1A1A] font-medium">Nessun documento</div>
        <div className="text-[12px] text-[#6B6B6B] mt-1">Carica libretto sanitario, vaccini, fatture vet.</div>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="docs-list">
      {docs.map((d) => {
        const dt = d.expires_at ? daysTo(d.expires_at) : null;
        const exp = dt !== null && dt < 0;
        const soon = dt !== null && dt >= 0 && dt <= 30;
        const accent = exp ? "#E94545" : soon ? "#FFB020" : "#2DBA60";
        return (
          <motion.div
            key={d.id}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            data-testid={`doc-row-${d.id}`}
            className="relative rounded-2xl p-3.5 bg-white border border-[#F0EBE3] flex items-center gap-3 overflow-hidden"
            style={{ boxShadow: exp ? `0 0 0 1px ${accent}33, 0 6px 18px -10px ${accent}55` : "none" }}
          >
            {exp && (
              <div aria-hidden className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />
            )}
            <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: `${accent}1F`, color: accent }}>
              <FileText className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] text-[#1A1A1A] font-medium leading-tight truncate">{d.title}</div>
              <div className="text-[11px] text-[#6B6B6B] mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
                <span className="uppercase tracking-wider">{DOC_CAT_LABEL[d.category] || d.category}</span>
                {d.expires_at && (
                  <>
                    <span className="text-[#E5DECF]">·</span>
                    <span style={{ color: accent }} className="font-medium">
                      {exp ? `Scaduto da ${Math.abs(dt)}gg` : soon ? `Scade tra ${dt}gg` : `Scade ${new Date(d.expires_at).toLocaleDateString("it-IT")}`}
                    </span>
                  </>
                )}
              </div>
            </div>
            {d.file_data && (
              <a href={d.file_data} target="_blank" rel="noreferrer"
                className="text-[11px] text-[#6B6B6B] hover:text-[#1A1A1A] px-2 py-1 rounded-full bg-[#F4EFE6]">
                Apri
              </a>
            )}
            <button onClick={() => onDelete(d.id)} aria-label="Elimina" className="text-[#9A9A9A] hover:text-[#E94545] -m-1 p-1.5">
              <Trash2 className="h-4 w-4" />
            </button>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ───── REMINDERS (modernized warm theme) ───── */
export function Reminders({ dogId, dogs, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reminders");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    await api.post(`/reminders/${id}/complete`);
    hapticSuccess();
    toast.success("Promemoria completato");
    load(); onChange?.();
    dispatchOrio(ORIO_EVENTS.REMINDERS_CHANGED);
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare il promemoria?")) return;
    await api.delete(`/reminders/${id}`);
    load(); onChange?.();
    dispatchOrio(ORIO_EVENTS.REMINDERS_CHANGED);
  };

  const testPush = async (id) => {
    try {
      const { data } = await api.post(`/reminders/${id}/test-push`);
      if (data.delivered) toast.success("Notifica push inviata · controlla il device");
      else toast.message("Nessun device sottoscritto · attiva le notifiche nel profilo");
    } catch {
      toast.error("Errore invio test push");
    }
  };

  const quickAdd = async (q) => {
    try {
      await api.post("/reminders", {
        title: q.title, type: q.type, due_date: ADD_MONTHS(q.inMonths),
        dog_id: dogId || null,
      });
      hapticSuccess();
      toast.success(`+ ${q.title}`);
      load(); onChange?.();
      dispatchOrio(ORIO_EVENTS.REMINDERS_CHANGED);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const filtered = (dogId
    ? items.filter((r) => !r.dog_id || r.dog_id === dogId)
    : items
  ).filter((r) => !r.completed_at);

  const today = new Date();
  const daysTo = (d) => Math.ceil((new Date(d) - today) / (1000 * 60 * 60 * 24));

  return (
    <div className="space-y-4" data-testid="reminders-tab">
      {/* Quick-add chips */}
      <div className="rounded-2xl p-3.5 bg-white border border-[#F0EBE3]" data-testid="quick-reminders">
        <div className="text-[10px] uppercase tracking-[0.20em] text-[#9A9A9A] font-semibold mb-2 inline-flex items-center gap-1.5">
          <Sparkles className="h-3 w-3 text-[#FF7A1A]" /> Aggiunta rapida
        </div>
        <div className="flex flex-wrap gap-1.5">
          {QUICK_REMINDERS.map((q, i) => (
            <motion.button
              key={i}
              whileTap={{ scale: 0.96 }}
              data-testid={`quick-rem-${q.type}-${q.inMonths}`}
              onClick={() => quickAdd(q)}
              className="px-3 py-1.5 rounded-full text-[12px] inline-flex items-center gap-1.5 border"
              style={{
                background: `${q.color}10`, color: q.color, borderColor: `${q.color}33`,
              }}
            >
              <q.icon className="h-3.5 w-3.5" /> {q.title}
            </motion.button>
          ))}
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={() => setOpen(true)}
            data-testid="open-custom-reminder"
            className="px-3 py-1.5 rounded-full text-[12px] inline-flex items-center gap-1.5 border border-dashed border-[#E5DECF] text-[#6B6B6B] hover:bg-[#F4EFE6]"
          >
            <Plus className="h-3.5 w-3.5" /> Personalizzato
          </motion.button>
        </div>
      </div>

      {loading ? (
        <div className="text-[#9A9A9A] text-sm py-8 text-center">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl p-8 sm:p-10 text-center bg-white border border-[#F0EBE3]">
          <Bell className="h-8 w-8 mx-auto text-[#C7C0B3]" />
          <div className="mt-3 text-[#1A1A1A] font-medium">Nessun promemoria attivo</div>
          <div className="text-[12px] text-[#6B6B6B] mt-1">Usa un&apos;aggiunta rapida sopra o crea uno personalizzato.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((r) => {
            const t = REM_BY[r.type] || REM_BY.altro;
            const d = daysTo(r.due_date);
            const overdue = d < 0;
            const soon = d >= 0 && d <= 7;
            const accent = overdue ? "#E94545" : soon ? "#FFB020" : t.color;
            return (
              <ReminderCard
                key={r.id}
                r={r} t={t} d={d} accent={accent} overdue={overdue}
                dogs={dogs}
                onRemove={remove}
                onComplete={complete}
                onTestPush={testPush}
              />
            );
          })}
        </div>
      )}

      {open && (
        <ReminderDialog
          dogs={dogs}
          defaultDogId={dogId}
          onClose={() => setOpen(false)}
          onDone={() => { setOpen(false); load(); onChange?.(); }}
        />
      )}
    </div>
  );
}

function ReminderCard({ r, t, d, accent, overdue, dogs, onRemove, onComplete, onTestPush }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`reminder-card-${r.id}`}
      className="relative rounded-2xl p-4 bg-white border overflow-hidden"
      style={{
        borderColor: overdue ? `${accent}55` : "#F0EBE3",
        boxShadow: overdue ? `0 0 0 1px ${accent}22, 0 8px 22px -14px ${accent}66` : "0 4px 18px -12px rgba(0,0,0,0.10)",
      }}>
      {overdue && (
        <motion.div aria-hidden
          animate={{ opacity: [0.4, 0.7, 0.4] }} transition={{ duration: 2, repeat: Infinity }}
          className="absolute -top-3 -right-3 h-14 w-14 rounded-full"
          style={{ background: `radial-gradient(circle, ${accent}40 0%, transparent 65%)` }} />
      )}
      <div className="relative flex items-start justify-between gap-2">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center"
          style={{ background: `${accent}1F`, color: accent }}>
          <t.icon className="h-4 w-4" />
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onTestPush(r.id)} aria-label="Test push"
            data-testid={`reminder-test-${r.id}`}
            className="text-[#9A9A9A] hover:text-[#FF7A1A] -m-1 p-1" title="Invia notifica di test">
            <BellRing className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => onRemove(r.id)} aria-label="Elimina"
            className="text-[#9A9A9A] hover:text-[#E94545] -m-1 p-1">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-2.5 text-[10px] uppercase tracking-widest" style={{ color: accent }}>
        {t.label}
      </div>
      <div className="font-medium text-sm mt-0.5 leading-tight text-[#1A1A1A]">{r.title}</div>
      {r.dog_id && (
        <div className="text-[11px] text-[#6B6B6B] mt-0.5">
          🐾 {dogs.find((dg) => dg.id === r.dog_id)?.name || ""}
        </div>
      )}
      <div className="mt-3 flex items-center gap-1.5 text-xs">
        <Calendar className="h-3 w-3 text-[#9A9A9A]" />
        <span className="font-medium" style={{ color: accent }}>
          {overdue ? `⚠️ Scaduto da ${Math.abs(d)}gg` : d === 0 ? "Oggi" : d === 1 ? "Domani" : `Tra ${d} giorni`}
        </span>
      </div>
      <div className="text-[11px] text-[#9A9A9A] mt-0.5">
        {new Date(r.due_date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
      </div>
      {r.notes && <div className="text-[11px] text-[#6B6B6B] mt-2 line-clamp-2">{r.notes}</div>}
      <button
        data-testid={`reminder-complete-${r.id}`}
        onClick={() => onComplete(r.id)}
        className="mt-3 w-full px-3 py-2 rounded-full text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 text-white"
        style={{
          background: `linear-gradient(135deg, ${accent} 0%, ${accent}DD 100%)`,
          boxShadow: `0 8px 20px -10px ${accent}66`,
        }}>
        <Check className="h-3.5 w-3.5" /> Segna come fatto
      </button>
    </motion.div>
  );
}

/* ─────  Upload + Reminder Dialogs (kept warm-themed) ───── */
export function UploadDialog({ dogId, autoScan = false, defaultCategory = "vaccino", onClose, onDone }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = !!(user?.is_pro || user?.role === "admin");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(defaultCategory);
  const [file, setFile] = useState("");
  const [expires, setExpires] = useState("");
  const [notes, setNotes] = useState("");
  const [keyData, setKeyData] = useState(null);  // structured OCR extract
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  // If autoScan is on (user tapped the hero AI Scan CTA), open the
  // native camera immediately when the dialog mounts.
  useEffect(() => {
    if (autoScan && isPro) {
      // Small delay so the dialog is painted first
      const t = setTimeout(() => cameraRef.current?.click(), 200);
      return () => clearTimeout(t);
    }
  }, [autoScan, isPro]);

  const readFile = (f) => new Promise((res, rej) => {
    if (!f) return rej("no file");
    if (f.size > 5 * 1024 * 1024) return rej("Max 5MB");
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej("read error");
    r.readAsDataURL(f);
  });

  const onFile = async (e) => {
    try {
      const f = e.target.files?.[0];
      const url = await readFile(f);
      setFile(url);
      setScanned(false);
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Errore caricamento");
    }
  };

  // ── OCR: sends the image to /documents/scan and prefills the form
  const runScan = async () => {
    if (!file) return toast.error("Prima carica una foto del documento");
    if (!isPro) {
      setShowPaywall(true);
      return;
    }
    setScanning(true);
    hapticTap();
    try {
      const { data } = await api.post("/documents/scan", { file_data: file }, { skipGlobalPaywall: true });
      // Prefill form
      if (data.title) setTitle(data.title);
      if (data.category) setCategory(data.category);
      if (data.expires_at) setExpires(data.expires_at);
      if (data.notes) setNotes(data.notes);
      setKeyData(data.key_data || null);
      setScanned(true);
      hapticSuccess();
      toast.success(
        data.confidence === "high"
          ? "Dati estratti · precisione alta"
          : data.confidence === "medium"
            ? "Dati estratti · verifica i campi"
            : "Estrazione parziale · completa i campi mancanti"
      );
    } catch (err) {
      const st = err.response?.status;
      const detail = err.response?.data?.detail;
      if (st === 402) {
        setShowPaywall(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Errore scansione");
      }
    } finally {
      setScanning(false);
    }
  };

  const submit = async () => {
    if (!title.trim() || !file) return toast.error("Titolo e file obbligatori");
    setLoading(true);
    try {
      const { data } = await api.post("/documents", {
        dog_id: dogId, title, category, file_data: file,
        expires_at: expires || null,
        notes: notes || null,
        key_data: keyData || null,
      }, { skipGlobalPaywall: true });
      toast.success("Caricato");
      if (data?.expense_created) {
        const ex = data.expense_created;
        toast.success(`Spesa aggiunta · €${ex.amount_eur.toFixed(2)} ${EXPENSE_BY[ex.category]?.label || ""}`);
        dispatchOrio(ORIO_EVENTS.DOCUMENTS_CHANGED);
      }
      dispatchOrio(ORIO_EVENTS.DOCUMENTS_CHANGED);
      onDone();
    } catch (e) {
      const st = e.response?.status;
      const detail = e.response?.data?.detail;
      if (st === 402) setShowPaywall(true);
      else toast.error(typeof detail === "string" ? detail : "Errore caricamento");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[1200] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-4 pb-[160px] sm:pb-4" onClick={onClose}>
      <div className="rounded-3xl w-full max-w-md p-5 sm:p-6 bg-white border border-[#F0EBE3] max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-[0.20em] text-[#9A9A9A]">Cassetto</div>
        <h3 className="font-display text-2xl mb-4 mt-1">Carica documento</h3>

        {/* ─── AI SCAN CTA ─── */}
        {!file ? (
          <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              data-testid="doc-scan-camera"
              onClick={() => {
                if (!isPro) { setShowPaywall(true); return; }
                cameraRef.current?.click();
              }}
              className="relative rounded-2xl p-4 border border-[#F0EBE3] bg-gradient-to-br from-[#FF7A1A]/10 to-[#F4B83C]/10 hover:from-[#FF7A1A]/15 hover:to-[#F4B83C]/15 transition-all text-left"
            >
              <div className="h-9 w-9 rounded-xl flex items-center justify-center mb-2"
                style={{ background: "linear-gradient(135deg, #FF7A1A, #F4B83C)" }}>
                <Camera className="h-5 w-5 text-white" />
              </div>
              <div className="text-[13px] font-semibold text-[#1A1A1A]">Fotografa</div>
              <div className="text-[11px] text-[#6B6B6B] mt-0.5">Scatta col telefono</div>
              {!isPro && (
                <span className="absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#1A1A1A] text-white">PRO</span>
              )}
            </button>
            <button
              data-testid="doc-upload-gallery"
              onClick={() => fileRef.current?.click()}
              className="rounded-2xl p-4 border border-[#F0EBE3] bg-white hover:bg-[#F4EFE6] transition-all text-left"
            >
              <div className="h-9 w-9 rounded-xl flex items-center justify-center mb-2 bg-[#F0EBE3]">
                <FileText className="h-5 w-5 text-[#6B6B6B]" />
              </div>
              <div className="text-[13px] font-semibold text-[#1A1A1A]">Carica file</div>
              <div className="text-[11px] text-[#6B6B6B] mt-0.5">Da galleria o PDF</div>
            </button>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="doc-camera-input" />
            <input ref={fileRef} type="file" onChange={onFile} accept="image/*,application/pdf" className="hidden" data-testid="doc-file" />
          </div>
        ) : (
          <div className="mb-4 relative rounded-2xl overflow-hidden border border-[#F0EBE3] bg-[#F4EFE6]">
            {file.startsWith("data:image/") ? (
              <img src={file} alt="preview" className="w-full h-40 object-cover" />
            ) : (
              <div className="p-6 text-center text-[13px] text-[#6B6B6B]">
                <FileText className="h-8 w-8 mx-auto text-[#9A9A9A] mb-1" />
                File caricato
              </div>
            )}
            <button
              onClick={() => { setFile(""); setScanned(false); setKeyData(null); }}
              className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur"
              aria-label="Rimuovi"
            >
              <X className="h-4 w-4" />
            </button>
            {file.startsWith("data:image/") && !scanned && (
              <button
                onClick={runScan}
                disabled={scanning}
                data-testid="doc-scan-run"
                className="absolute bottom-2 left-2 right-2 py-2 rounded-xl text-[12.5px] font-semibold text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-70"
                style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)", boxShadow: "0 8px 20px -8px rgba(255,122,26,0.5)" }}
              >
                {scanning ? <><Loader2 className="h-4 w-4 animate-spin" /> Sto leggendo…</> : <><Zap className="h-4 w-4" /> {isPro ? "Estrai dati con AI" : "Estrai dati con AI · Pro"}</>}
              </button>
            )}
            {scanned && (
              <div className="absolute bottom-2 left-2 right-2 py-1.5 rounded-xl text-[11px] font-semibold text-white inline-flex items-center justify-center gap-1"
                style={{ background: "rgba(34,197,94,0.9)" }}>
                <Sparkles className="h-3 w-3" /> Dati estratti · verifica sotto
              </div>
            )}
          </div>
        )}

        <input data-testid="doc-title" placeholder="Titolo (es. Vaccino antirabbica 2026)"
          value={title} onChange={(e) => setTitle(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-[#FBF7F1] border border-[#F0EBE3] outline-none mb-3 text-[#1A1A1A]" />
        <select data-testid="doc-category" value={category} onChange={(e) => setCategory(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-[#FBF7F1] border border-[#F0EBE3] outline-none mb-3 text-[#1A1A1A]">
          {DOC_CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} placeholder="Scadenza (opzionale)"
          className="w-full px-4 py-3 rounded-xl bg-[#FBF7F1] border border-[#F0EBE3] outline-none mb-3 text-[#1A1A1A]" />
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (opzionale)" maxLength={500}
          className="w-full px-4 py-3 rounded-xl bg-[#FBF7F1] border border-[#F0EBE3] outline-none mb-3 min-h-[60px] text-[#1A1A1A] text-[13px]" />

        {/* Extracted key data preview */}
        {keyData && Object.keys(keyData).filter((k) => keyData[k]).length > 0 && (
          <div className="rounded-xl p-3 mb-3 bg-[#FFF7ED] border border-[#F4B83C]/40">
            <div className="text-[10px] uppercase tracking-widest text-[#B37016] font-bold mb-1.5 inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> Dati estratti dall'AI
            </div>
            <div className="grid grid-cols-2 gap-1.5 text-[11.5px]">
              {Object.entries(keyData)
                .filter(([, v]) => v !== null && v !== "" && v !== undefined)
                .map(([k, v]) => (
                  <div key={k} className="text-[#4A3B1F]">
                    <span className="text-[#8A7147] font-medium capitalize">{k.replace(/_/g, " ")}: </span>
                    <span className="font-semibold">{String(v)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 px-5 py-3 rounded-full bg-[#F4EFE6] text-sm border border-[#F0EBE3]">Annulla</button>
          <button onClick={submit} disabled={loading} data-testid="doc-submit"
            className="flex-1 px-5 py-3 rounded-full text-white text-sm font-semibold disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}>
            {loading ? "…" : "Carica"}
          </button>
        </div>
      </div>

      <Paywall
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        feature="la scansione AI dei documenti"
        benefits={[
          "Fotografa qualunque documento sanitario",
          "L'AI estrae vet, vaccino, lotto, microchip, scadenza",
          "Cassetto illimitato + PDF passaporto UE",
          "Promemoria intelligenti · notifiche push",
        ]}
      />
    </div>
  );
}

/* ───── WEIGHT TIMELINE — mini chart of weight entries from Diary ───── */
export function WeightTimeline({ entries, dog, isPro, onOpenDiary }) {
  if (!entries || entries.length === 0) return null;
  const values = entries.map((e) => Number(e.value));
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = Math.max(0.5, max - min);
  const latest = entries[entries.length - 1];
  const prev = entries.length > 1 ? entries[entries.length - 2] : null;
  const delta = prev ? Number(latest.value) - Number(prev.value) : 0;
  const trend = delta > 0.1 ? "up" : delta < -0.1 ? "down" : "stable";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid="weight-timeline"
      className="mb-4 rounded-2xl p-4 bg-white border border-[#F0EBE3] overflow-hidden relative"
      style={{ boxShadow: "0 4px 18px -10px rgba(0,0,0,0.08)" }}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.20em] text-[#9A9A9A] font-semibold inline-flex items-center gap-1.5">
            <Weight className="h-3 w-3 text-[#0EA5E9]" /> Peso {dog?.name || ""}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-display text-3xl text-[#1A1A1A] tabular-nums leading-none">{latest.value}</span>
            <span className="text-[13px] text-[#6B6B6B]">kg</span>
            {trend !== "stable" && (
              <span
                className="ml-1 text-[11px] font-semibold inline-flex items-center gap-0.5"
                style={{ color: trend === "up" ? "#E56F1A" : "#2DBA60" }}
              >
                {trend === "up" ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}kg
              </span>
            )}
          </div>
          <div className="text-[11px] text-[#9A9A9A] mt-0.5">
            {new Date(latest.happened_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
            {" · "}
            {entries.length} rilevazion{entries.length === 1 ? "e" : "i"}
          </div>
        </div>
        <button
          onClick={onOpenDiary}
          className="text-[11px] font-semibold text-[#FF7A1A] inline-flex items-center gap-1 hover:underline"
        >
          Aggiungi <Plus className="h-3 w-3" />
        </button>
      </div>

      {/* Mini sparkline SVG */}
      <div className="relative h-16">
        <svg viewBox="0 0 300 64" preserveAspectRatio="none" className="w-full h-full">
          <defs>
            <linearGradient id="wgrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0" />
            </linearGradient>
          </defs>
          {entries.length > 1 && (() => {
            const points = entries.map((e, i) => {
              const x = (i / Math.max(1, entries.length - 1)) * 300;
              const y = 60 - ((Number(e.value) - min) / range) * 50 - 4;
              return { x, y };
            });
            const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
            const area = `${path} L300,64 L0,64 Z`;
            return (
              <>
                <path d={area} fill="url(#wgrad)" />
                <path d={path} fill="none" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                {points.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r={i === points.length - 1 ? 4 : 2.5}
                    fill={i === points.length - 1 ? "#0EA5E9" : "white"}
                    stroke="#0EA5E9" strokeWidth="1.5" />
                ))}
              </>
            );
          })()}
          {entries.length === 1 && (
            <circle cx="150" cy="32" r="5" fill="#0EA5E9" />
          )}
        </svg>
      </div>
    </motion.div>
  );
}

function ReminderDialog({ dogs, defaultDogId, onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("vaccino");
  const [dueDate, setDueDate] = useState("");
  const [dogId, setDogId] = useState(defaultDogId || "");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !dueDate) return;
    setLoading(true);
    try {
      await api.post("/reminders", { title, type, due_date: dueDate, dog_id: dogId || null, notes: notes || null });
      toast.success("Promemoria aggiunto");
      dispatchOrio(ORIO_EVENTS.REMINDERS_CHANGED);
      onDone();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1500] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6 pb-[160px] sm:pb-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="reminder-dialog"
        className="rounded-3xl w-full max-w-md p-5 sm:p-6 space-y-3 bg-white border border-[#F0EBE3]">
        <div className="text-[10px] uppercase tracking-[0.20em] text-[#9A9A9A]">Nuovo promemoria</div>
        <h3 className="font-display text-2xl text-[#1A1A1A]">Cosa devi ricordare?</h3>
        <div className="grid grid-cols-3 gap-1.5">
          {REM_TYPES.slice(0, 6).map((t) => (
            <button type="button" key={t.id} data-testid={`rem-type-${t.id}`} onClick={() => setType(t.id)}
              className="rounded-xl p-2 flex flex-col items-center gap-1 text-[10px] font-medium border"
              style={{
                background: type === t.id ? `${t.color}1F` : "white",
                borderColor: type === t.id ? `${t.color}55` : "#F0EBE3",
                color: type === t.id ? t.color : "#6B6B6B",
              }}>
              <t.icon className="h-4 w-4" />
              <span className="leading-tight">{t.label}</span>
            </button>
          ))}
        </div>
        <input data-testid="rem-title" type="text" required maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Titolo (es. Richiamo Rabbia)"
          className="w-full px-4 py-3 rounded-xl bg-[#FBF7F1] border border-[#F0EBE3] outline-none text-[#1A1A1A]" />
        <input data-testid="rem-date" type="date" required value={dueDate} onChange={(e) => setDueDate(e.target.value)}
          min={new Date().toISOString().slice(0, 10)}
          className="w-full px-4 py-3 rounded-xl bg-[#FBF7F1] border border-[#F0EBE3] outline-none text-[#1A1A1A]" />
        {dogs.length > 0 && (
          <select data-testid="rem-dog" value={dogId} onChange={(e) => setDogId(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-[#FBF7F1] border border-[#F0EBE3] outline-none text-[#1A1A1A]">
            <option value="">Tutti i miei cani</option>
            {dogs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
        <textarea data-testid="rem-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Note (opzionale)" maxLength={500}
          className="w-full px-4 py-3 rounded-xl bg-[#FBF7F1] border border-[#F0EBE3] outline-none min-h-[60px] text-[#1A1A1A]" />
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-5 py-3 rounded-full bg-[#F4EFE6] text-sm border border-[#F0EBE3]">Annulla</button>
          <button type="submit" disabled={loading} data-testid="rem-submit"
            className="flex-1 px-5 py-3 rounded-full text-white text-sm font-semibold disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}>
            {loading ? "Salvataggio…" : "Aggiungi"}
          </button>
        </div>
      </form>
    </div>
  , document.body);
}
